/**
 * Faza va kimyoviy muvozanat — ideal yondashuvlar.
 *
 * TENGLAMALAR
 *   Raoult:      y_i·P = x_i·P_i^sat(T)   →  K_i = P_i^sat/P
 *   Qaynash T:   Σ x_i·K_i(T) = 1          (bisection bo'yicha T)
 *   Shudring T:  Σ y_i/K_i(T) = 1
 *   van't Hoff:  ln(Kp(T)/Kp(T0)) = −ΔH_r/R · (1/T − 1/T0)   (ΔH_r = const)
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. Ideal gaz va ideal eritma (aktivlik koeffitsienti γ = 1, fugitivlik φ = 1).
 *      Azeotrop aralashmalar (etanol–suv) uchun XATO beradi — faqat o'quv
 *      masalalarida ideal juftliklar (benzol–toluol) uchun.
 *   2. ΔH_r haroratga bog'liq emas (Kirchhoff tuzatishi yo'q).
 *   3. P^sat Antoine bo'yicha — modda uchun Antoine bo'lmasa PropertyUnavailableError.
 *
 * QO'LLANISH CHEGARASI: Antoine oralig'i; P < 0.5·Pc.
 * MANBA: Smith, Van Ness, Abbott — "Introduction to Chemical Engineering
 * Thermodynamics", 7-nashr, 10 va 13-boblar.
 */
import { R_GAS, saturationPressure } from '../properties';
import { bisection } from '../solvers';
import type { SolverResult } from '../solvers';
import type { WarningSink } from '../types';

/** Raoult bo'yicha muvozanat konstantasi K_i = P_i^sat(T)/P */
export function raoultK(substance: string, T: number, P: number, warnings?: WarningSink): number {
  return saturationPressure(substance, T, warnings) / P;
}

/** Qaynash harorati: Σ x_i K_i = 1. `x` — molyar ulushlar (yig'indisi 1). */
export function bubbleT(x: Record<string, number>, P: number, range: [number, number] = [200, 700]): SolverResult {
  return bisection((T) => Object.entries(x).reduce((s, [id, xi]) => s + xi * raoultK(id, T, P), 0) - 1, range[0], range[1], { xtol: 1e-6, ftol: 1e-9 });
}

/** Shudring harorati: Σ y_i / K_i = 1 */
export function dewT(y: Record<string, number>, P: number, range: [number, number] = [200, 700]): SolverResult {
  return bisection((T) => Object.entries(y).reduce((s, [id, yi]) => s + yi / raoultK(id, T, P), 0) - 1, range[0], range[1], { xtol: 1e-6, ftol: 1e-9 });
}

/** Qaynash T da bug' tarkibi y_i = x_i K_i (normallashtirilgan) */
export function vaporComposition(x: Record<string, number>, T: number, P: number): Record<string, number> {
  const y: Record<string, number> = {};
  let sum = 0;
  for (const [id, xi] of Object.entries(x)) {
    y[id] = xi * raoultK(id, T, P);
    sum += y[id];
  }
  for (const id of Object.keys(y)) y[id] = (y[id] as number) / sum;
  return y;
}

/**
 * Muvozanat konstantasi Kp(T) van't Hoff bo'yicha.
 * @param Kp0 T0 dagi Kp; @param dHr reaksiya issiqligi, J/mol (manfiy — ekzotermik)
 */
export function kpVantHoff(Kp0: number, T0: number, dHr: number, T: number): number {
  return Kp0 * Math.exp((-dHr / R_GAS) * (1 / T - 1 / T0));
}

/** Kp ni ΔG° dan: Kp = exp(−ΔG°/(R·T)) */
export function kpFromGibbs(dG: number, T: number): number {
  return Math.exp(-dG / (R_GAS * T));
}
